import { useEffect } from 'react';
import { item, track } from '../analytics/analytics';
import { useAnalyticsConsent } from '../analytics/useAnalyticsConsent';
import { Link } from 'react-router-dom';
import { brandCopy } from '@skytech/shared';

import { useProducts, useFeaturedProducts } from '../products/api';
import { HeroCarousel } from './HeroCarousel';
import { HomeValueProps } from './HomeValueProps';
import { CategoryCarousel } from './CategoryCarousel';
import { ProductCard } from '../../components/ui/ProductCard';
import { ProductCardSkeleton } from '../../components/ui/Skeleton';

/** Homepage (SRS §4.2): hero, value props, categories, and product rails. */
export function HomePage(): JSX.Element {
  const newest = useProducts({ sort: 'newest', limit: 8 });
  const products = newest.data?.pages[0]?.data ?? [];
  const featured = useFeaturedProducts();
  const featuredProducts = (featured.data ?? []).slice(0, 4);
  const arrivals = products.filter((p) => !featuredProducts.some((f) => f.id === p.id)).slice(0, 4);

  return (
    <div data-page-ready={!newest.isLoading && !newest.isError && !featured.isLoading}>
      {/* Hero — admin-managed banner carousel (falls back to the branded default). */}
      <HeroCarousel />

      {(featured.isLoading || featuredProducts.length > 0) && (
        <ProductSection
          title="Featured products"
          products={featuredProducts}
          loading={featured.isLoading}
        />
      )}
      {/* Shop by category — admin-curated, compact grid with counts. */}
      <CategoryCarousel />

      <HomeValueProps />
      {newest.isError && (
        <div role="alert" className="mx-auto max-w-7xl p-6 text-sm text-neutral">
          Products could not load.{' '}
          <button className="min-h-11 text-sky-ink underline" onClick={() => void newest.refetch()}>
            Try again
          </button>
        </div>
      )}
      {(newest.isLoading || arrivals.length > 0) && (
        <ProductSection title="New arrivals" products={arrivals} loading={newest.isLoading} />
      )}

      {/* Certified-partner strip + value props — closing reassurance rows, so the
          Shop-by-category and product content surface first below the hero. */}
      <div className="border-t border-frost bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-2 px-6 py-4 text-xs tracking-[0.15em] text-neutral">
          <span className="uppercase text-sky-ink">Certified partner</span>
          {brandCopy.partners.map((p) => (
            <span key={p}>{p}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductSection({
  title,
  products,
  loading,
}: {
  title: string;
  products: import('@skytech/shared').Product[];
  loading: boolean;
}): JSX.Element {
  const consentVersion = useAnalyticsConsent();
  const listId = title === 'Featured products' ? 'home-featured' : 'home-newest';
  useEffect(() => {
    if (products.length)
      track(
        'view_item_list',
        { item_list_id: listId, items: products.map((p) => item(p)) },
        `${listId}:${products.map((p) => p.id).join(',')}`,
      );
  }, [products, listId, consentVersion]);
  return (
    <section className="mx-auto max-w-7xl px-4 py-5 md:px-6 md:py-8">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-light text-navy-900 sm:text-2xl">{title}</h2>
        <Link
          to="/products"
          className="inline-flex min-h-11 items-center text-sm text-sky-ink hover:underline"
        >
          View all →
        </Link>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {loading
          ? Array.from({ length: 4 }, (_, i) => <ProductCardSkeleton key={i} />)
          : products.map((p, i) => (
              <ProductCard
                key={p.id}
                product={p}
                listId={listId}

                priority={title === 'Featured products' && i < 2}
              />
            ))}
      </div>
    </section>
  );
}
